import {
	AnnouncementGlobalSearchResultItem,
	AppGlobalSearchResultItem,
	CommunityGlobalSearchResultItem,
	CommunityPostGlobalSearchResultItem,
	CommunityPostGlobalSearchResultItemStatisticsModel,
	ContactGlobalSearchResultItem,
	DocumentGlobalSearchResultItem,
	EgnyteGlobalSearchResultItem,
	EmailGlobalSearchResultItem,
	EventGlobalSearchResultItem,
	GlobalSearchResultItemKind,
	GlobalSearchScopeIdentifier,
	IGlobalSearchResultItem,
	IntegrationFlowGlobalSearchResultItem,
	KnowledgeItemGlobalSearchResultItem,
	MaintenanceWindowAppModel
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	AppColorPersistedModel,
	AppGlobalSearchItemPersistedModel,
	AppImagePersistedModel,
	CommunityPostGlobalSearchResultItemStatisticsPersistedModel,
	DocumentGlobalSearchItemPersistedModel,
	DocumentPermissionsPersistenceModel,
	GlobalSearchItemPersistedModel,
	HubItemCategoryListItemPersistedModel,
	HubItemMediaPersistedModel,
	ImageFocalPointPersistedModel,
	ListLocationPersistedModel,
	MaintenanceWindowPersistedModel,
	ReactionCounterPersistedModel,
	TagPersistedModel,
	UserIdentityPersistedModel,
	UserPersistedModel
} from "./models";
import {
	AppColorKind,
	AppColorViewModel,
	AppType
} from "client/Api/Apps/common";
import { never } from "client_legacy/ndaw/errors";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { TagViewModel } from "client/Api/Tags/tags";
import {
	DocumentItemType,
	FileViewModel,
	ListLocationViewModel,
	Permissions
} from "client/Api/Documents/SharePoint/common";
import {
	HubItemMediaKind,
	HubItemMediaPreviewViewModel,
	ImageFocalPointModel,
	ReactionCounterModel
} from "client/Api/Hub/common";
import { HubItemCategoryListItemModel } from "client/Api/Hub/hub";
import { PreviewItemCategoryModel, PreviewItemUserModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { ISearchResultItem } from "../../SearchResults/MultiScopeSearch/MultiScopeSearch";
import { UserIdentityModel } from "client/Api/UserProfile/service";

export function convertForPersistence(
	resultItem:
		| IGlobalSearchResultItem
		| ISearchResultItem,
	itemScope: GlobalSearchScopeIdentifier
): GlobalSearchItemPersistedModel {
	const baseItem: BaseGlobalSearchItemPersistedModel = {
		itemScope
	};

	switch (resultItem.kind) {
		case GlobalSearchResultItemKind.App:
			return convertAppForPersistence(resultItem, baseItem);
		case GlobalSearchResultItemKind.Document:
			return convertDocumentForPersistence(resultItem, baseItem);
		case GlobalSearchResultItemKind.Email:
			return convertEmailForPersistence(resultItem, baseItem);
		case GlobalSearchResultItemKind.Contact:
			return convertContactForPersistence(resultItem, baseItem);
		case GlobalSearchResultItemKind.Announcement:
			return convertAnnouncementForPersistence(resultItem, baseItem);
		case GlobalSearchResultItemKind.KnowledgeItems:
			return convertKnowledgeItemForPersistence(resultItem, baseItem);
		case GlobalSearchResultItemKind.EgnyteItem:
			return convertEgnyteForPersistence(resultItem, baseItem);
		case GlobalSearchResultItemKind.Event:
			return convertEventForPersistence(resultItem, baseItem);
		case GlobalSearchResultItemKind.IntegrationFlow:
			return convertIntegrationFlowForPersistence(resultItem, baseItem);
		case GlobalSearchResultItemKind.Community:
			return convertCommunityForPersistence(resultItem, baseItem);
		case GlobalSearchResultItemKind.CommunityPost:
			return convertCommunityPostForPersistence(resultItem, baseItem);
		default:
			never(resultItem);
	}
}

function convertAppForPersistence(
	resultItem: AppGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.App,
		app: {
			...convertAppSpecificParameters(resultItem),
			appType: resultItem.appType,
			id: resultItem.id,
			name: resultItem.name,
			color: convertColorForPersistence(resultItem.color),
			image: convertAppImageForPersistence(resultItem.image),
			category: resultItem.category,
			maintenanceWindow: convertAppMaintenanceWindowForPersistence(resultItem.maintenanceWindow),
			tags: resultItem.tags.map(convertTagForPersistence),
		}
	};
}

function convertDocumentForPersistence(
	resultItem: DocumentGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	const {
		document
	} = resultItem;

	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.Document,
		document: {
			itemType: document.itemType,
			id: document.id,
			name: document.name,
			parentFolderId: document.parentFolderId,
			createdOn: document.createdOn,
			modifiedOn: document.modifiedOn,
			viewId: document.viewId,
			serverRelativeUrl: document.serverRelativeUrl,
			createdBy: document.createdBy,
			modifiedBy: document.modifiedBy,
			extension: document.extension,
			location: convertListLocationForPermission(document.location),
			permissions: convertDocumentPermissionsForPersistence(document.permissions),
			bookmarkId: document.bookmarkId,
			file: document.itemType === DocumentItemType.File
				? convertFileForPersistence(document)
				: undefined
		}
	};
}

function convertEmailForPersistence(
	resultItem: EmailGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	const {
		item: email,
		parentFolderName
	} = resultItem;

	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.Email,
		email: {
			emailId: email.emailId
				.id,
			from: email.from,
			subject: email.subject,
			receivedDateTime: email.receivedDateTime,
			isFlagged: email.isFlagged,
			hasAttachments: email.hasAttachments,
			ownerName: email.ownerName,
			lastAction: email.lastAction,
			importance: email.importance,
			isMeetingMessage: email.isMeetingMessage,
			preview: email.preview,
			parentFolderName: parentFolderName,
			color: convertColorForPersistence(resultItem.color)
		}
	};
}

function convertContactForPersistence(
	resultItem: ContactGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	const {
		contact,
		contactSource
	} = resultItem;

	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.Contact,
		contact: {
			id: contact.id,
			firstName: contact.firstName,
			middleName: contact.middleName,
			lastName: contact.lastName,
			displayName: contact.displayName,
			jobTitle: contact.jobTitle,
			phoneNumber: contact.phoneNumber,
			emailAddress: contact.emailAddress,
			company: contact.company,
			department: contact.department,
			officeLocation: contact.officeLocation,
			photoUrl: contact.photoUrl,
			presenceStatus: contact.presenceStatus,
			skills: contact.skills?.map(t => convertTagForPersistence(t)),
			contactSource
		}
	};
}

function convertAnnouncementForPersistence(
	resultItem: AnnouncementGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	const {
		announcement
	} = resultItem;

	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.Announcement,
		announcement: {
			id: announcement.id,
			title: announcement.title,
			descriptionPreview: announcement.descriptionPreview,
			media: convertHubMediaForPersistence(announcement.media),
			publicationStart: announcement.publicationStart,
			categories: announcement.categories.map(convertAnnouncementCategoryForPersistence),
			authorName: announcement.authorName,
			reactionCounters: announcement.reactionCounters.map(convertReactionCounterForPersistence),
			commentsCount: announcement.commentsCount
		}
	};
}

function convertKnowledgeItemForPersistence(
	resultItem: KnowledgeItemGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	const {
		knowledgeItem
	} = resultItem;

	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.KnowledgeItems,
		knowledgeItem: {
			id: knowledgeItem.id,
			author: convertUserForPersistence(knowledgeItem.author),
			category: convertHubCategoryForPersistence(knowledgeItem.category),
			descriptionPreview: knowledgeItem.descriptionPreview,
			lastModifiedDate: knowledgeItem.lastModifiedDate,
			media: convertHubMediaForPersistence(knowledgeItem.media),
			title: knowledgeItem.title,
			viewsCount: knowledgeItem.viewsCount
		}
	};
}

function convertEgnyteForPersistence(
	resultItem: EgnyteGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.EgnyteItem,
		egnyteItem: {
			extension: resultItem.extension,
			lastModifiedDate: resultItem.lastModifiedDate,
			locationUrl: resultItem.locationUrl,
			modifiedBy: resultItem.modifiedBy,
			name: resultItem.name,
			path: resultItem.path,
			snippet: resultItem.snippet,
			url: resultItem.url
		}
	};
}

function convertEventForPersistence(
	resultItem: EventGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	const {
		event
	} = resultItem;

	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.Event,
		event: {
			id: event.id,
			attendeeCount: event.attendeeCount,
			author: convertUserForPersistence(event.author),
			category: convertHubCategoryForPersistence(event.category),
			descriptionPreview: event.descriptionPreview,
			location: event.location,
			locationType: event.locationType,
			media: convertHubMediaForPersistence(event.media),
			startDate: event.startDate,
			title: event.title
		}
	};
}

function convertIntegrationFlowForPersistence(
	resultItem: IntegrationFlowGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.IntegrationFlow,
		integrationFlow: {
			color: convertColorForPersistence(resultItem.color),
			dateTime: resultItem.dateTime,
			description: resultItem.description,
			icon: convertAppImageForPersistence(resultItem.icon),
			link: resultItem.link,
			subTitle1: resultItem.subTitle1,
			subTitle2: resultItem.subTitle2,
			title: resultItem.title
		}
	};
}

function convertCommunityForPersistence(
	resultItem: CommunityGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.Community,
		community: {
			id: resultItem.id,
			isCurrentUserMember: resultItem.isCurrentUserMember,
			memberCount: resultItem.memberCount,
			name: resultItem.name,
			color: convertColorForPersistence(resultItem.color)
		}
	};
}

function convertCommunityPostForPersistence(
	resultItem: CommunityPostGlobalSearchResultItem,
	baseItem: BaseGlobalSearchItemPersistedModel
): GlobalSearchItemPersistedModel {
	return {
		...baseItem,
		kind: GlobalSearchResultItemKind.CommunityPost,
		communityPost: {
			id: resultItem.id,
			communityId: resultItem.community.id,
			communityName: resultItem.community.name,
			createdBy: convertUserIdentityForPersistence(resultItem.createdBy),
			createdOn: resultItem.createdOn,
			image: resultItem.images[0] ?? null,
			messagePreview: resultItem.messagePreview,
			statistics: convertCommunityPostStatisticsForPersistence(resultItem.statistics)
		}
	};
}

type AppSpecificParameters = Pick<
	AppGlobalSearchItemPersistedModel,
	| "destinationWindow"
	| "emailDestinationApp"
	| "calendarDestinationApp"
	| "clientlessRdpDestinationWindow"
	| "windowsVirtualDesktopClient"
>;

function convertAppSpecificParameters(resultItem: AppGlobalSearchResultItem): AppSpecificParameters {
	switch (resultItem.appType) {
		case AppType.Office365Word:
		case AppType.Office365Excel:
		case AppType.Office365PowerPoint:
		case AppType.Office365OneNote:
		case AppType.Office365LyncMeeting:
		case AppType.Office365PeopleScreen:
		case AppType.CitrixXenApp:
		case AppType.Yammer:
		case AppType.Embed:
		case AppType.AddressBook:
		case AppType.Hub:
		case AppType.LocalApp:
			return { };
		case AppType.Autotask:
		case AppType.AzureApp:
		case AppType.Documents:
		case AppType.TopDesk:
		case AppType.Url:
		case AppType.VilansKick:
		case AppType.ConnectWise:
		case AppType.Google:
		case AppType.SharePoint:
		case AppType.Teams:
		case AppType.ToDo:
		case AppType.Liquit:
		case AppType.Magister:
		case AppType.NedapOns:
		case AppType.Afas:
		case AppType.VmwareHorizonApp:
		case AppType.Zaaksysteem:
		case AppType.Zermelo:
		case AppType.ZenyaDoc:
			return {
				destinationWindow: resultItem.parameters
					.destinationWindow
			};
		case AppType.Email:
			return {
				destinationWindow: resultItem.parameters
					.destinationWindow,
				emailDestinationApp: resultItem.parameters
					.destinationApp
			};
		case AppType.Office365Calendar:
			return {
				destinationWindow: resultItem.parameters
					.destinationWindow,
				calendarDestinationApp: resultItem.parameters
					.destinationApp
			};
		case AppType.ClientlessRdp:
			return {
				clientlessRdpDestinationWindow: resultItem.parameters
					.destinationWindow
			};
		case AppType.WindowsVirtualDesktop:
			return {
				windowsVirtualDesktopClient: resultItem.parameters
					.clientKind
			};
		default:
			never(resultItem);
	}
}

function convertColorForPersistence(color: AppColorViewModel): AppColorPersistedModel {
	switch (color.kind) {
		case AppColorKind.Custom:
			return {
				kind: AppColorKind.Custom,
				customValue: color.value
			};
		case AppColorKind.Header:
		case AppColorKind.Inherit:
		case AppColorKind.Primary:
			return {
				kind: color.kind
			};
		default:
			never(color);
	}
}

function convertAppImageForPersistence(appImage: AppImageDisplayInfo): AppImagePersistedModel {
	return {
		renderType: appImage.renderType,
		imageUrl: appImage.imageUrl
	};
}

function convertAppMaintenanceWindowForPersistence(
	maintenanceWindow: MaintenanceWindowAppModel | null
): MaintenanceWindowPersistedModel | null {
	if (!maintenanceWindow) {
		return null;
	}

	return {
		endsOn: maintenanceWindow.endsOn,
		notificationMessage: maintenanceWindow.notificationMessage
	};
}

function convertTagForPersistence(tag: TagViewModel): TagPersistedModel {
	return {
		id: tag.id,
		name: tag.name
	};
}

function convertListLocationForPermission(location: ListLocationViewModel): ListLocationPersistedModel {
	return {
		listId: location.listId,
		siteUrl: location.siteUrl
	};
}

function convertDocumentPermissionsForPersistence(permissions: Permissions): DocumentPermissionsPersistenceModel {
	return {
		canCreate: permissions.canCreate,
		canDelete: permissions.canDelete,
		canDownload: permissions.canDownload,
		canEdit: permissions.canEdit,
		canEnumeratePermissions: permissions.canEnumeratePermissions,
		canManagePermissions: permissions.canManagePermissions,
		canView: permissions.canView
	};
}

function convertFileForPersistence(file: FileViewModel): DocumentGlobalSearchItemPersistedModel["file"] {
	return {
		size: file.size,
		checkInComment: file.checkInComment,
		isInfected: file.isInfected,
		labelAppliedBy: file.labelAppliedBy,
		labelAppliedDateTime: file.labelAppliedDateTime,
		labels: file.labels,
		labelSetting: file.labelSetting
	};
}

function convertHubMediaForPersistence(media: HubItemMediaPreviewViewModel): HubItemMediaPersistedModel {
	return {
		mediaKind: media.mediaKind,
		image: media.mediaKind === HubItemMediaKind.Image
			? {
				imageId: media.imageId,
				imageName: media.imageName,
				focalPoint: convertFocalPointForPersistence(media.focalPoint)
			}
			: undefined,
		video: media.mediaKind === HubItemMediaKind.Video
			? {
				url: media.videoUrl
			}
			: undefined
	};
}

function convertFocalPointForPersistence(focalPoint: ImageFocalPointModel): ImageFocalPointPersistedModel {
	return {
		left: focalPoint.left,
		top: focalPoint.top
	};
}

function convertAnnouncementCategoryForPersistence(category: HubItemCategoryListItemModel): HubItemCategoryListItemPersistedModel {
	return {
		id: category.id,
		name: category.name
	};
}

function convertReactionCounterForPersistence(model: ReactionCounterModel): ReactionCounterPersistedModel {
	return {
		kind: model.kind,
		count: model.count
	};
}

function convertUserIdentityForPersistence(userIdentity: UserIdentityModel | null): UserIdentityPersistedModel | null {
	if (!userIdentity) {
		return null;
	}

	return {
		avatarId: userIdentity.avatarId,
		department: userIdentity.department,
		firstName: userIdentity.firstName,
		jobTitle: userIdentity.jobTitle,
		lastName: userIdentity.lastName,
		officeLocation: userIdentity.officeLocation,
		pronouns: userIdentity.pronouns,
		status: userIdentity.status,
		userId: userIdentity.userId
	};
}

function convertUserForPersistence(user: PreviewItemUserModel | null): UserPersistedModel | null {
	if (!user) {
		return null;
	}

	return {
		id: user.id,
		name: user.name
	};
}

function convertHubCategoryForPersistence(category: PreviewItemCategoryModel | null): HubItemCategoryListItemPersistedModel | null {
	if (!category) {
		return null;
	}

	return {
		id: category.id,
		name: category.name
	};
}

function convertCommunityPostStatisticsForPersistence(
	statistics: CommunityPostGlobalSearchResultItemStatisticsModel
): CommunityPostGlobalSearchResultItemStatisticsPersistedModel {
	return {
		commentsCount: statistics.commentsCount,
		reactionCounters: statistics.reactionCounters.map(convertReactionCounterForPersistence)
	};
}

type BaseGlobalSearchItemPersistedModel = Pick<GlobalSearchItemPersistedModel, "itemScope">;
